import { getDomainFromUrl } from '../../utils/utils';
import { isUsableWebsite, isValidWebsiteUrl, normalizeUrl } from '../../utils/urlMetadata';
import { AGGREGATOR_HOSTS } from './constants';
import { SourceInput, SourceRow } from './types';

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_SCHEME_RE = /^https?:\/\//i;

const toSafeUrl = (rawUrl?: string): { url: string; hostname: string } | null => {
    const raw = rawUrl?.trim();
    if (!raw || !isUsableWebsite(raw)) return null;
    if (SCHEME_RE.test(raw) && !HTTP_SCHEME_RE.test(raw)) return null;
    const url = normalizeUrl(raw);
    if (!isValidWebsiteUrl(url)) return null;
    const { hostname } = new URL(url);
    return hostname.includes('.') ? { url, hostname } : null;
};

const toSourceRow = ({ url: rawUrl, name, subHeading }: SourceInput): Omit<SourceRow, 'key'> | null => {
    const safe = toSafeUrl(rawUrl);
    if (!safe) return null;
    const { url, hostname } = safe;
    const isAggregator = AGGREGATOR_HOSTS.includes(hostname);
    const heading = name?.trim() || (isAggregator ? '' : getDomainFromUrl(url));
    if (!heading) return null;
    return { heading, subHeading: subHeading?.trim() ?? '', url, isAggregator };
};

const toDuplicateKey = ({ url, heading, subHeading }: Omit<SourceRow, 'key'>) => [url, heading, subHeading].join('\n').toLowerCase();

export const buildSourceRows = (inputs?: SourceInput[]): SourceRow[] => {
    const rowsByDuplicateKey = new Map<string, Omit<SourceRow, 'key'>>();
    (inputs ?? []).forEach((input) => {
        const row = toSourceRow(input);
        if (row && !rowsByDuplicateKey.has(toDuplicateKey(row))) rowsByDuplicateKey.set(toDuplicateKey(row), row);
    });
    return Array.from(rowsByDuplicateKey.values(), (row, index) => ({ ...row, key: `${index}:${row.url}` }));
};
